(() => {
  const coverageBlocks = Array.from(document.querySelectorAll('[data-redo-coverage]'));

  if (!coverageBlocks.length) return;

  const coverageModals = coverageBlocks
    .map((block) => block.querySelector('[data-redo-coverage-modal]'))
    .filter(Boolean);
  const coverageModalFocusableSelector = [
    'a[href]',
    'button:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  coverageModals.forEach((modal) => {
    if (modal.parentElement !== document.body) document.body.appendChild(modal);
  });

  const REDO_STORE_ID = '65721a025f017b0006b31618';
  const REDO_WIDGET_SLUG = 'qr69wpatxphctl5';
  const REDO_API_URL = `https://api.getredo.com/v2.2/stores/${REDO_STORE_ID}/coverage-products`;
  const REDO_WIDGET_URL = `https://shopify-cdn.getredo.com/widgets/${REDO_WIDGET_SLUG}`;
  const REDO_WIDGET_CONFIG_URL = `${REDO_WIDGET_URL}/autocheck`;
  const REDO_WIDGET_FEES_URL = `${REDO_WIDGET_URL}/fees`;
  const REDO_CHECKOUT_CONFIG_URL = 'https://shopify-cdn.getredo.com/widgets/autocheck';
  const REDO_CART_ATTRIBUTE = 'redo_opted_in_from_cart';
  const REDO_EXCHANGE_STORAGE_KEY = 'redo.exchange-select';
  const REDO_PRODUCT_IDS = new Set(['7818317365428']);
  const REDO_PRODUCT_HANDLE = 'free-unlimited-returns-for-store-credit-or-exchanges-package-protection-2';
  const REDO_SKU_PATTERN = /^x-redo/i;
  const REDO_VENDOR_PATTERN = /^redo$/i;
  const REDO_TITLE_PATTERN = /(package protection|free refunds|free returns|unlimited returns)/i;
  const CART_URL = '/cart.js';
  const CART_ADD_URL = '/cart/add.js';
  const CART_UPDATE_URL = '/cart/update.js';
  const CHECKOUT_URL = '/checkout';

  let currentCoverage = null;
  let currentCart = null;
  let widgetConfigPromise = null;
  let widgetFeesPromise = null;
  const checkoutConfigRequests = new Map();
  const localizedCoverageProductRequests = new Map();
  let coverageRequestId = 0;
  let isCheckingOut = false;
  let activeCoverageModal = null;
  let coverageModalLastFocusedElement = null;
  let coverageModalCloseTimer = null;

  const fallbackCheckoutButtons = () => Array.from(document.querySelectorAll('[data-redo-fallback-checkout]'));

  const setCoverageModalToggleState = (modal, expanded) => {
    if (!modal?.id) return;

    document.querySelectorAll('[data-redo-coverage-info-toggle]').forEach((toggle) => {
      if (toggle.getAttribute('aria-controls') === modal.id) {
        toggle.setAttribute('aria-expanded', String(expanded));
      }
    });
  };

  const getCoverageModalFocusableElements = (modal) => {
    if (!modal) return [];

    return Array.from(modal.querySelectorAll(coverageModalFocusableSelector)).filter((element) => {
      return element instanceof HTMLElement && !element.hidden && element.offsetParent !== null;
    });
  };

  const closeCoverageModal = ({ restoreFocus = true } = {}) => {
    const modal = activeCoverageModal;
    if (!modal || modal.hidden) return;

    modal.classList.remove('redo-coverage-modal--open');
    modal.setAttribute('aria-hidden', 'true');
    setCoverageModalToggleState(modal, false);
    document.documentElement.classList.remove('redo-coverage-modal-is-open');
    document.body.classList.remove('redo-coverage-modal-is-open');
    activeCoverageModal = null;

    window.clearTimeout(coverageModalCloseTimer);
    coverageModalCloseTimer = window.setTimeout(() => {
      modal.hidden = true;
    }, 180);

    if (restoreFocus && coverageModalLastFocusedElement) {
      coverageModalLastFocusedElement.focus({ preventScroll: true });
    }
  };

  const openCoverageModal = (modal, trigger) => {
    if (!modal) return;

    if (activeCoverageModal && activeCoverageModal !== modal) {
      closeCoverageModal({ restoreFocus: false });
    }

    window.clearTimeout(coverageModalCloseTimer);
    coverageModalLastFocusedElement = trigger || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    activeCoverageModal = modal;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    setCoverageModalToggleState(modal, true);
    document.documentElement.classList.add('redo-coverage-modal-is-open');
    document.body.classList.add('redo-coverage-modal-is-open');

    window.requestAnimationFrame(() => {
      modal.classList.add('redo-coverage-modal--open');
      const closeButton = modal.querySelector('.redo-coverage-modal__close');
      const panel = modal.querySelector('[role="dialog"]');
      (closeButton || panel || modal).focus({ preventScroll: true });
    });
  };

  const trapCoverageModalFocus = (event) => {
    if (event.key !== 'Tab' || !activeCoverageModal) return;

    const focusableElements = getCoverageModalFocusableElements(activeCoverageModal);
    if (!focusableElements.length) {
      event.preventDefault();
      activeCoverageModal.querySelector('[role="dialog"]')?.focus({ preventScroll: true });
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  const isRedoExchangeSession = () => {
    try {
      return window.sessionStorage.getItem(REDO_EXCHANGE_STORAGE_KEY) !== null;
    } catch (error) {
      return false;
    }
  };

  const getCheckoutCountry = () => {
    const country = document.querySelector('[data-checkout-country]')?.dataset.checkoutCountry ||
      window.Shopify?.country ||
      '';

    const normalizedCountry = String(country).trim().toUpperCase();

    return /^[A-Z]{2}$/.test(normalizedCountry) ? normalizedCountry : '';
  };

  const getCheckoutUrl = () => {
    const country = getCheckoutCountry();

    if (!country) return CHECKOUT_URL;

    const url = new URL(CHECKOUT_URL, window.location.origin);
    url.searchParams.set('country', country);

    return `${url.pathname}${url.search}`;
  };

  const getCartCheckoutAction = (trigger) => {
    const sourceAction = trigger?.closest('form')?.getAttribute('action');
    const routeRoot = String(window.Shopify?.routes?.root || '/');
    const normalizedRoot = routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`;
    const url = new URL(sourceAction || `${normalizedRoot}cart`, window.location.origin);
    const country = getCheckoutCountry();

    if (country) url.searchParams.set('country', country);

    return `${url.pathname}${url.search}`;
  };

  const toStringId = (value) => {
    if (value === null || value === undefined) return '';

    const match = String(value).match(/(\d+)$/);
    return match ? match[1] : String(value);
  };

  const toMoneyAmount = (cents) => {
    return (Math.max(Number(cents || 0), 0) / 100).toFixed(2);
  };

  const normalizeCurrency = (currency) => {
    return String(currency || '').trim().toUpperCase();
  };

  const getCurrency = (cart) => {
    return normalizeCurrency(cart?.currency || document.querySelector('[data-cart-drawer]')?.dataset.currency) || 'USD';
  };

  const formatMoneyAmount = (amount, currency) => {
    const numericAmount = Number(amount || 0);

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency
      }).format(numericAmount);
    } catch (error) {
      return `$${numericAmount.toFixed(2)}`;
    }
  };

  const getResponseError = async (response, fallbackMessage) => {
    const error = await response.json().catch(() => ({}));

    return new Error(error.description || error.message || fallbackMessage);
  };

  const requestJson = async (url, options = {}, fallbackMessage = 'The bag could not be updated.') => {
    const response = await fetch(url, {
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {})
      },
      ...options
    });

    if (!response.ok) throw await getResponseError(response, fallbackMessage);
    return response.json();
  };

  const requestCart = () => {
    return requestJson(CART_URL, {}, 'Could not load your bag.');
  };

  const postCartJson = (url, body, fallbackMessage) => {
    return requestJson(url, {
      method: 'POST',
      body: JSON.stringify(body)
    }, fallbackMessage);
  };

  const requestWidgetConfig = () => {
    if (!widgetConfigPromise) {
      widgetConfigPromise = requestJson(REDO_WIDGET_CONFIG_URL, {}, 'Could not load Redo settings.');
    }

    return widgetConfigPromise;
  };

  const requestWidgetFees = () => {
    if (!widgetFeesPromise) {
      widgetFeesPromise = requestJson(REDO_WIDGET_FEES_URL, {}, 'Could not load Redo fees.');
    }

    return widgetFeesPromise;
  };

  const getShopifyStoreUrl = () => {
    return String(window.Shopify?.shop || 'wild-oak-boutique1.myshopify.com').trim();
  };

  const requestCheckoutConfig = (currency) => {
    const normalizedCurrency = normalizeCurrency(currency) || 'USD';

    if (normalizedCurrency === 'USD') return requestWidgetConfig();
    if (checkoutConfigRequests.has(normalizedCurrency)) {
      return checkoutConfigRequests.get(normalizedCurrency);
    }

    const url = new URL(REDO_CHECKOUT_CONFIG_URL);

    url.searchParams.set('storeUrl', getShopifyStoreUrl());
    url.searchParams.set('foreign_currency', normalizedCurrency);

    const request = requestJson(String(url), {}, 'Could not load localized Redo settings.');

    checkoutConfigRequests.set(normalizedCurrency, request);
    request.catch(() => checkoutConfigRequests.delete(normalizedCurrency));

    return request;
  };

  const getLocalizedCoverageProductUrl = (handle) => {
    const routeRoot = String(window.Shopify?.routes?.root || '/');
    const normalizedRoot = routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`;

    return `${normalizedRoot}products/${encodeURIComponent(handle || REDO_PRODUCT_HANDLE)}.js`;
  };

  const requestLocalizedCoverageProduct = (handle, currency) => {
    const productHandle = String(handle || REDO_PRODUCT_HANDLE).trim();
    const cacheKey = `${currency}:${productHandle}`;

    if (localizedCoverageProductRequests.has(cacheKey)) {
      return localizedCoverageProductRequests.get(cacheKey);
    }

    const fetchTransport = window.__wobNativeFetch || window.fetch?.bind(window);

    if (!fetchTransport) {
      return Promise.reject(new Error('Localized Redo pricing is unavailable.'));
    }

    const request = fetchTransport(getLocalizedCoverageProductUrl(productHandle), {
      headers: { Accept: 'application/json' }
    }).then(async (response) => {
      if (!response.ok) {
        throw await getResponseError(response, 'Localized Redo pricing is unavailable.');
      }

      return response.json();
    });

    localizedCoverageProductRequests.set(cacheKey, request);
    request.catch(() => localizedCoverageProductRequests.delete(cacheKey));

    return request;
  };

  const isRedoLineItem = (item) => {
    if (!item) return false;

    const productId = toStringId(item.product_id);
    const sku = String(item.sku || '');
    const vendor = String(item.vendor || '');
    const url = String(item.url || item.handle || '');
    const title = String(item.product_title || item.title || '');

    return REDO_PRODUCT_IDS.has(productId)
      || REDO_SKU_PATTERN.test(sku)
      || REDO_VENDOR_PATTERN.test(vendor)
      || url.includes(REDO_PRODUCT_HANDLE)
      || (REDO_TITLE_PATTERN.test(title) && REDO_SKU_PATTERN.test(sku));
  };

  const getCartItems = (cart) => {
    return Array.isArray(cart?.items) ? cart.items : [];
  };

  const getSellableCartItems = (cart) => {
    return getCartItems(cart).filter((item) => !isRedoLineItem(item));
  };

  const getRedoCart = (cart) => {
    const currency = getCurrency(cart);
    const items = getSellableCartItems(cart);
    const totalCents = items.reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);

    return {
      lineItems: items.map((item) => ({
        id: String(item.key || item.id || item.variant_id),
        quantity: Number(item.quantity || 0),
        product: {
          id: toStringId(item.product_id)
        },
        variant: {
          id: toStringId(item.variant_id || item.id)
        },
        priceTotal: {
          amount: toMoneyAmount(item.final_line_price),
          currency
        },
        originalPrice: {
          amount: toMoneyAmount(item.original_line_price || item.line_price || item.final_line_price),
          currency
        },
        coupons: []
      })),
      priceTotal: {
        amount: toMoneyAmount(totalCents),
        currency
      }
    };
  };

  const getCoveragePriceAmount = (coverageProduct) => {
    return coverageProduct?.price?.amount
      || coverageProduct?.cartInfoToEnable?.selectedVariant?.price?.amount
      || '';
  };

  const selectCoverageProduct = (data) => {
    const products = Array.isArray(data?.coverageProducts) ? data.coverageProducts : [];

    return products.find((product) => {
      const cartInfo = product?.cartInfoToEnable || {};
      const variantId = toStringId(cartInfo.variantId || cartInfo.selectedVariant?.id);

      return variantId && cartInfo.selectedVariant?.availableForSale !== false;
    }) || null;
  };

  const normalizeCoverage = (coverageProduct, cart) => {
    if (!coverageProduct) return null;

    const cartInfo = coverageProduct.cartInfoToEnable || {};
    const variantId = toStringId(cartInfo.variantId || cartInfo.selectedVariant?.id);
    const priceAmount = getCoveragePriceAmount(coverageProduct);
    const currency = normalizeCurrency(
      coverageProduct.price?.currency ||
      coverageProduct.price?.currencyCode ||
      cartInfo.selectedVariant?.price?.currencyCode ||
      getCurrency(cart)
    );

    if (!variantId || !priceAmount) return null;

    return {
      cartAttribute: cartInfo.cartAttribute || REDO_CART_ATTRIBUTE,
      currency,
      priceAmount,
      priceText: formatMoneyAmount(priceAmount, currency),
      productHandle: cartInfo.selectedVariant?.product?.handle || REDO_PRODUCT_HANDLE,
      productId: toStringId(cartInfo.productId || cartInfo.selectedVariant?.product?.id),
      variantId
    };
  };

  const requestOfficialCoverage = async (cart) => {
    const redoCart = getRedoCart(cart);

    if (!redoCart.lineItems.length) return null;

    const response = await fetch(REDO_API_URL, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ cart: redoCart })
    });

    if (!response.ok) throw await getResponseError(response, 'Redo coverage is not available right now.');

    const data = await response.json();
    return normalizeCoverage(selectCoverageProduct(data), cart);
  };

  const getRuleSetForCountry = (ruleSets, country) => {
    const sets = Array.isArray(ruleSets) ? ruleSets : [];

    return sets.find((set) => Array.isArray(set.countries) && set.countries.includes(country))
      || sets.find((set) => !Array.isArray(set.countries) || set.countries.length === 0)
      || sets[0]
      || null;
  };

  const getActiveBracket = (priceBrackets, cartValue) => {
    const brackets = Array.isArray(priceBrackets)
      ? [...priceBrackets].sort((a, b) => Number(a.pricePoint || 0) - Number(b.pricePoint || 0))
      : [];

    if (!brackets.length) return null;

    if (!cartValue) return brackets[0];

    for (let index = 0; index < brackets.length; index += 1) {
      const bracket = brackets[index];
      const nextBracket = brackets[index + 1];
      const pricePoint = Number(bracket.pricePoint || 0);
      const nextPricePoint = Number(nextBracket?.pricePoint || 0);

      if (!nextBracket || (cartValue > pricePoint && cartValue <= nextPricePoint)) {
        return bracket;
      }
    }

    return brackets[brackets.length - 1];
  };

  const clampProtectionPrice = (price, settings) => {
    let nextPrice = Number(price || 0);
    const minPrice = Number(settings?.minPrice);
    const maxPrice = Number(settings?.maxPrice);

    if (Number.isFinite(minPrice)) nextPrice = Math.max(nextPrice, minPrice);
    if (Number.isFinite(maxPrice)) nextPrice = Math.min(nextPrice, maxPrice);

    return Math.max(nextPrice, 0);
  };

  const getPackageProtectionPrice = (settings, cartValue, country) => {
    if (!settings?.coverageEnabled) return 0;

    const ruleSet = getRuleSetForCountry(settings.pricingRuleSets, country);
    const bracket = getActiveBracket(ruleSet?.priceBrackets, cartValue);

    if (!ruleSet || !bracket) return 0;

    if (ruleSet.type === 'percentage') {
      return clampProtectionPrice(cartValue * Number(bracket.value || 0), settings);
    }

    return clampProtectionPrice(bracket.value, settings);
  };

  const findFeeVariant = (fees, priceAmount) => {
    const variants = Array.isArray(fees?.bothProduct?.variants) ? fees.bothProduct.variants : [];
    const targetCents = Math.round(Number(priceAmount || 0) * 100);

    return variants.find((variant) => Math.round(Number(variant.price || 0) * 100) === targetCents) || null;
  };

  const getInternationalReturnPrice = (config, country) => {
    const basePrice = Number(config?.pricePerOrder || 0);
    const coveredCountries = Array.isArray(config?.countries)
      ? config.countries
      : [config?.countries].filter(Boolean);
    const internationalPricing = Array.isArray(config?.internationalReturnsPricing)
      ? config.internationalReturnsPricing
      : [];

    if (!basePrice || !internationalPricing.length || !coveredCountries.length || coveredCountries.includes(country)) {
      return basePrice;
    }

    const pricing = internationalPricing.find((item) => {
      return Array.isArray(item?.countries) && item.countries.includes(country);
    }) || internationalPricing.find((item) => {
      return !Array.isArray(item?.countries) || item.countries.length === 0;
    });
    const multiplier = Number(pricing?.multiplier || 1);

    return basePrice * multiplier;
  };

  const floorCurrency = (value) => {
    return Math.floor(Math.max(Number(value || 0), 0) * 100) / 100;
  };

  const selectLocalizedFeeVariant = (product, priceAmount, country) => {
    const variants = (Array.isArray(product?.variants) ? product.variants : [])
      .filter((variant) => variant?.available !== false && Number.isFinite(Number(variant?.price)))
      .map((variant) => ({
        ...variant,
        localizedPrice: Number(variant.price) / 100
      }))
      .sort((a, b) => a.localizedPrice - b.localizedPrice);
    const targetPrice = Number(priceAmount || 0);

    if (!variants.length || !Number.isFinite(targetPrice)) return null;

    let low = 0;
    let high = variants.length - 1;

    while (low <= high) {
      const middle = Math.floor((low + high) / 2);
      const middlePrice = variants[middle].localizedPrice;

      if (middlePrice === targetPrice) {
        low = middle;
        break;
      }

      if (middlePrice < targetPrice) {
        low = middle + 1;
      } else {
        high = middle - 1;
      }
    }

    if (low >= variants.length) low = variants.length - 1;
    if (low === 0) return variants[low];

    const upperVariant = variants[low];
    const lowerVariant = variants[Math.max(0, low - 1)];
    const upperDifference = Math.abs(upperVariant.localizedPrice - targetPrice);
    const lowerDifference = Math.abs(lowerVariant.localizedPrice - targetPrice);
    const tolerance = country === 'US' ? 1 : Math.max(targetPrice * 0.35, 1);

    if (upperDifference >= tolerance && lowerDifference < upperDifference) {
      return lowerVariant;
    }

    return upperVariant;
  };

  const requestWidgetCoverage = async (cart, officialCoverage = null) => {
    const redoCart = getRedoCart(cart);

    if (!redoCart.lineItems.length) return null;

    const currency = getCurrency(cart);
    const country = getCheckoutCountry() || 'US';
    const config = await requestCheckoutConfig(currency);

    if (config?.enabled === false || config?.coverageEnabled === false) return null;

    const cartValue = Number(redoCart.priceTotal.amount || 0);
    const returnPrice = getInternationalReturnPrice(config, country);
    const protectionPrice = config?.packageProtectionAutoCheck === false
      ? 0
      : getPackageProtectionPrice(config?.packageProtection, cartValue, country);
    const targetPrice = floorCurrency(returnPrice + protectionPrice);
    let variant = null;
    let productId = officialCoverage?.productId;

    if (currency === 'USD') {
      const fees = await requestWidgetFees();

      if (fees?.enabled === false) return null;

      variant = findFeeVariant(fees, targetPrice);
      productId = toStringId(fees?.bothProduct?.productId || fees?.productId || productId);
    } else {
      const localizedProduct = await requestLocalizedCoverageProduct(officialCoverage?.productHandle, currency);

      variant = selectLocalizedFeeVariant(localizedProduct, targetPrice, country);
      productId = toStringId(localizedProduct?.id || productId);
    }

    const variantId = toStringId(variant?.variant_id || variant?.id);
    const priceAmount = currency === 'USD'
      ? Number(variant?.price || targetPrice).toFixed(2)
      : toMoneyAmount(variant?.price);

    if (!variantId) return null;

    return {
      cartAttribute: officialCoverage?.cartAttribute || REDO_CART_ATTRIBUTE,
      currency,
      priceAmount,
      priceText: formatMoneyAmount(priceAmount, currency),
      productId,
      variantId
    };
  };

  const requestCoverage = async (cart) => {
    const officialResult = await Promise.allSettled([requestOfficialCoverage(cart)]);
    const officialCoverage = officialResult[0].status === 'fulfilled' ? officialResult[0].value : null;
    const officialRequestSucceeded = officialResult[0].status === 'fulfilled';
    const cartCurrency = getCurrency(cart);

    if (officialRequestSucceeded && officialCoverage === null) return null;
    if (!officialRequestSucceeded && cartCurrency !== 'USD') return null;

    if (officialCoverage && officialCoverage.currency !== cartCurrency) return null;

    try {
      return await requestWidgetCoverage(cart, officialCoverage);
    } catch (error) {
      console.warn(error);
      return null;
    }
  };

  const updateFallbackButtons = (coverageAvailable) => {
    fallbackCheckoutButtons().forEach((button) => {
      button.hidden = coverageAvailable;
      button.classList.toggle('redo-coverage__fallback--hidden', coverageAvailable);
      button.style.display = coverageAvailable ? 'none' : '';
      button.setAttribute('aria-hidden', coverageAvailable ? 'true' : 'false');

      if (isCheckingOut) {
        button.disabled = true;
      } else {
        button.disabled = false;
      }
    });
  };

  const clearCoverageErrors = () => {
    coverageBlocks.forEach((block) => {
      const error = block.querySelector('[data-redo-coverage-error]');

      if (!error) return;

      error.textContent = '';
      error.hidden = true;
    });
  };

  const showCoverageError = (message) => {
    coverageBlocks.forEach((block) => {
      const error = block.querySelector('[data-redo-coverage-error]');

      if (!error) return;

      error.textContent = message || 'Redo coverage could not be updated.';
      error.hidden = false;
    });
  };

  const renderUnavailable = () => {
    currentCoverage = null;

    if (activeCoverageModal) closeCoverageModal({ restoreFocus: false });

    coverageBlocks.forEach((block) => {
      block.hidden = true;
      block.removeAttribute('aria-busy');
    });

    updateFallbackButtons(false);
  };

  const renderCoverage = (coverage) => {
    if (!coverage) {
      renderUnavailable();
      return;
    }

    coverageBlocks.forEach((block) => {
      block.hidden = false;
      block.removeAttribute('aria-busy');

      block.querySelectorAll('[data-redo-coverage-price]').forEach((element) => {
        element.textContent = coverage.priceText;
      });

      block.querySelectorAll('[data-redo-coverage-button-price]').forEach((element) => {
        element.textContent = `+ ${coverage.priceText}`;
      });
    });

    clearCoverageErrors();
    updateFallbackButtons(true);
  };

  const setActionLoading = (trigger, loading) => {
    isCheckingOut = loading;

    coverageBlocks.forEach((block) => {
      if (loading) {
        block.setAttribute('aria-busy', 'true');
      } else {
        block.removeAttribute('aria-busy');
      }

      block.querySelectorAll('[data-redo-checkout-with], [data-redo-checkout-without]').forEach((button) => {
        const isTrigger = button === trigger;

        button.disabled = loading;
        button.classList.toggle('is-loading', loading && isTrigger);

        if (loading && isTrigger) {
          button.setAttribute('aria-busy', 'true');
        } else {
          button.removeAttribute('aria-busy');
        }
      });
    });

    updateFallbackButtons(Boolean(currentCoverage));
  };

  const refreshCoverage = async ({ cart = null } = {}) => {
    if (isRedoExchangeSession()) {
      renderUnavailable();
      return null;
    }

    const requestId = coverageRequestId + 1;
    coverageRequestId = requestId;

    try {
      let nextCart = cart || await requestCart();

      nextCart = await cleanStaleRedoCoverage(nextCart);
      const coverage = await requestCoverage(nextCart);

      if (requestId !== coverageRequestId) return null;

      currentCart = nextCart;
      currentCoverage = coverage;
      renderCoverage(coverage);
      return coverage;
    } catch (error) {
      if (requestId !== coverageRequestId) return null;

      console.warn(error);
      currentCoverage = null;
      renderUnavailable();
      return null;
    }
  };

  const getCartFormUpdates = (trigger) => {
    const form = trigger?.closest('form');

    if (!form || form.id !== 'CartPageForm') return null;

    const inputs = Array.from(form.querySelectorAll('input[name="updates[]"]'));

    if (!inputs.length) return null;

    return inputs.map((input) => {
      const quantity = Number.parseInt(input.value, 10);

      return Number.isFinite(quantity) ? Math.max(quantity, 0) : 0;
    });
  };

  const syncCartForm = async (trigger) => {
    const updates = getCartFormUpdates(trigger);

    if (!updates) return null;

    return postCartJson(CART_UPDATE_URL, { updates }, 'Could not update your bag.');
  };

  const getFreshCoverage = async (trigger) => {
    const requestId = coverageRequestId + 1;
    coverageRequestId = requestId;
    const syncedCart = await syncCartForm(trigger);
    const cart = syncedCart || await requestCart();
    const coverage = await requestCoverage(cart);

    if (requestId !== coverageRequestId) return { cart, coverage: null };

    currentCart = cart;
    currentCoverage = coverage;
    renderCoverage(coverage);

    return { cart, coverage };
  };

  const getRedoLineItems = (cart) => {
    return getCartItems(cart).filter(isRedoLineItem);
  };

  const removeRedoLineItems = async (cart) => {
    const updates = {};

    getRedoLineItems(cart).forEach((item) => {
      if (item.key) updates[item.key] = 0;
    });

    if (!Object.keys(updates).length) return cart;

    return postCartJson(CART_UPDATE_URL, { updates }, 'Could not remove previous Redo coverage.');
  };

  const updateRedoAttribute = (attributeName, value) => {
    return postCartJson(CART_UPDATE_URL, {
      attributes: {
        [attributeName || REDO_CART_ATTRIBUTE]: value
      }
    }, 'Could not update Redo coverage.');
  };

  const publishCartReplacement = (cart) => {
    document.dispatchEvent(new CustomEvent('cart:replace', { detail: { cart } }));
  };

  const cleanStaleRedoCoverage = async (cart) => {
    if (isCheckingOut || !getRedoLineItems(cart).length) return cart;

    let nextCart = await removeRedoLineItems(cart);

    nextCart = await updateRedoAttribute(REDO_CART_ATTRIBUTE, '');
    publishCartReplacement(nextCart);
    return nextCart;
  };

  const redirectToCheckout = (trigger) => {
    try {
      const form = document.createElement('form');
      const fields = {
        form_type: 'cart',
        utf8: '✓',
        checkout: 'Checkout'
      };

      form.method = 'post';
      form.action = getCartCheckoutAction(trigger);
      form.hidden = true;

      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement('input');

        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      HTMLFormElement.prototype.submit.call(form);
    } catch (error) {
      console.warn(error);
      window.location.assign(getCheckoutUrl());
    }
  };

  const checkoutWithCoverage = async (trigger) => {
    if (isCheckingOut) return;

    setActionLoading(trigger, true);
    clearCoverageErrors();

    try {
      const { cart, coverage } = await getFreshCoverage(trigger);

      if (!coverage) {
        throw new Error('Free returns are not available for this bag right now.');
      }

      await removeRedoLineItems(cart);
      await postCartJson(CART_ADD_URL, {
        id: Number(coverage.variantId),
        quantity: 1
      }, 'Could not add Redo coverage.');
      await updateRedoAttribute(coverage.cartAttribute, 'true');
      redirectToCheckout(trigger);
    } catch (error) {
      console.warn(error);
      showCoverageError(error.message);
      setActionLoading(trigger, false);
    }
  };

  const checkoutWithoutCoverage = async (trigger) => {
    if (isCheckingOut) return;

    setActionLoading(trigger, true);
    clearCoverageErrors();

    try {
      const syncedCart = await syncCartForm(trigger);
      const cart = syncedCart || currentCart || await requestCart();

      await removeRedoLineItems(cart);
      await updateRedoAttribute(currentCoverage?.cartAttribute || REDO_CART_ATTRIBUTE, '');
      redirectToCheckout(trigger);
    } catch (error) {
      console.warn(error);
      showCoverageError(error.message);
      setActionLoading(trigger, false);
    }
  };

  document.addEventListener('click', (event) => {
    const infoToggle = event.target.closest('[data-redo-coverage-info-toggle]');

    if (infoToggle) {
      event.preventDefault();
      openCoverageModal(document.getElementById(infoToggle.getAttribute('aria-controls')), infoToggle);
      return;
    }

    if (event.target.closest('[data-redo-coverage-modal-close]')) {
      event.preventDefault();
      closeCoverageModal();
      return;
    }

    const coverageButton = event.target.closest('[data-redo-checkout-with]');

    if (coverageButton) {
      event.preventDefault();

      if (isRedoExchangeSession()) {
        renderUnavailable();
        return;
      }

      checkoutWithCoverage(coverageButton);
      return;
    }

    const withoutCoverageButton = event.target.closest('[data-redo-checkout-without]');

    if (withoutCoverageButton) {
      event.preventDefault();
      checkoutWithoutCoverage(withoutCoverageButton);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!activeCoverageModal) return;

    if (event.key === 'Tab' || event.key === 'Escape') {
      event.stopPropagation();
    }

    trapCoverageModalFocus(event);

    if (event.key === 'Escape') {
      event.preventDefault();
      closeCoverageModal();
    }
  }, true);

  document.addEventListener('cart:updated', (event) => {
    refreshCoverage({ cart: event.detail?.cart });
  });

  window.addEventListener('pageshow', (event) => {
    const navigation = window.performance?.getEntriesByType?.('navigation')?.[0];
    const restoredFromHistory = event.persisted || navigation?.type === 'back_forward';

    if (!restoredFromHistory) return;

    coverageRequestId += 1;
    isCheckingOut = false;
    setActionLoading(null, false);
    clearCoverageErrors();
    refreshCoverage();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refreshCoverage());
  } else {
    refreshCoverage();
  }
})();
